/* global.js — progressive-enhancement custom elements + tiny helpers.
   Vanilla ES modules, no globals, no libraries. Every custom element defined
   here has an explicit `display` rule in base.css (custom elements default to
   display:inline, which swallows the box model). */

/* --- Tiny event bus for theme-wide events (cart:updated, drawer:open…) ----- */
export const bus = new EventTarget();
export function emit(name, detail) {
  bus.dispatchEvent(new CustomEvent(name, { detail }));
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

/* --- Shared scroll lock ---------------------------------------------------- */
/* body.drawer-lock is shared by the mobile drawer, the cart drawer and the age
   popup, and they can overlap — so the lock is reference-counted by owner
   element (a Set keeps repeated lock/unlock calls from the same owner
   idempotent). cart.js reaches it through window.themeScrollLock: asset URLs
   are CDN-versioned, so a relative ES import would execute a SECOND module
   instance with its own state (and duplicate listeners). */
const scrollLockOwners = new Set();
function applyScrollLock() {
  scrollLockOwners.forEach((owner) => {
    if (owner instanceof Node && !owner.isConnected) scrollLockOwners.delete(owner);
  });
  const locked = scrollLockOwners.size > 0;
  const root = document.documentElement;
  /* Measure the classic scrollbar BEFORE overflow: hidden reclaims its track —
     base.css pads that width back onto the body so the page (and the pinned
     header) does not jump sideways. Overlay scrollbars measure 0. A second
     owner locking on top of the first measures 0 too, hence the > 0 guard. */
  if (locked) {
    const gutter = window.innerWidth - root.clientWidth;
    if (gutter > 0) root.style.setProperty('--scrollbar-comp', `${gutter}px`);
  } else {
    root.style.removeProperty('--scrollbar-comp');
  }
  document.body.classList.toggle('drawer-lock', locked);
}
export function lockScroll(owner) {
  scrollLockOwners.add(owner || 'anonymous');
  applyScrollLock();
}
export function unlockScroll(owner) {
  scrollLockOwners.delete(owner || 'anonymous');
  applyScrollLock();
}
window.themeScrollLock = { lock: lockScroll, unlock: unlockScroll };

/* --- Focus trap helper ----------------------------------------------------- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
export function trapFocus(container, event) {
  const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  /* Neither dialog is focusable itself, so clicking non-focusable content inside
     one (a promo paragraph, a heading) drops activeElement to <body>. Without
     this branch the two below never match and Tab walks straight out into the
     page that aria-modal has hidden from AT. */
  if (!container.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* --- <header-nav> — sticky header, dropdowns, mobile drawer ---------------- */
class HeaderNav extends HTMLElement {
  /* details -> pending close timer. A Map (not one field) because several
     dropdowns can be closing at once: click-away and focusout both sweep every
     open panel, and each needs its own timer to cancel or fire. */
  #closeTimers = new Map();

  connectedCallback() {
    this.drawer = this.querySelector('[data-mobile-drawer]');
    /* Two toggles: the hamburger in the header row (it owns aria-expanded /
       aria-controls) and the close button inside the drawer — the drawer is
       aria-modal, so AT users must find a close control within it. */
    this.drawerToggles = Array.from(this.querySelectorAll('[data-drawer-toggle]'));
    this.drawerToggle =
      this.drawerToggles.find((btn) => btn.hasAttribute('aria-expanded')) || this.drawerToggles[0] || null;
    this.header = this.querySelector('[data-header]');

    /* The pin itself is CSS (.header-group-sticky in layout/theme.liquid wraps
       the whole header group so the announcement bar rides along). The merchant
       setting lives on this element, so mirror it onto that wrapper. */
    this.stickyWrap = this.closest('.header-group-sticky');
    if (this.stickyWrap) {
      this.stickyWrap.toggleAttribute('data-sticky-off', this.dataset.sticky !== 'true');
    }

    /* Two metrics about the chrome as a whole (announcement bar + header row —
       that is what sticks and what covers content):
       --header-height  how much viewport the chrome permanently takes away from
                        content: the group's height while pinned, 0 when the
                        merchant turned "Sticky header" off (nothing is pinned
                        then, so sections must not reserve a strip for it, and
                        neither must html { scroll-padding } in base.css).
                        Matches the 92px pinned default in css-variables.liquid;
                        header.liquid renders the 0 case server-side too.
       --header-bottom  how far down the viewport the header currently ends —
                        always the live measurement (it decays to 0 as an
                        unpinned header scrolls away). The mobile drawer offsets
                        itself by it, so it must stay truthful in both modes.
       Reads are batched into a frame because this also runs on scroll. */
    this.syncHeaderMetrics = () => {
      if (this.metricsFrame || !this.header) return;
      this.metricsFrame = window.requestAnimationFrame(() => {
        this.metricsFrame = 0;
        if (!this.header) return;
        const pinned = this.dataset.sticky === 'true';
        const height = pinned ? (this.stickyWrap || this.header).offsetHeight : 0;
        const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
        const root = document.documentElement.style;
        root.setProperty('--header-height', `${height}px`);
        root.setProperty('--header-bottom', `${Math.round(bottom)}px`);
      });
    };

    if ('ResizeObserver' in window && this.header) {
      this.resizeObserver = new ResizeObserver(this.syncHeaderMetrics);
      /* Watch the whole pinned group when it exists: the announcement bar can
         rewrap without the header's own box changing. */
      this.resizeObserver.observe(this.stickyWrap || this.header);
    }

    /* Scrolled state: the header is pinned by CSS sticky (it never leaves the
       flow, so no placeholder height is needed) — is-stuck only adds the
       scrolled shadow. */
    this.onScroll = () => {
      if (this.dataset.sticky === 'true') {
        this.classList.toggle('is-stuck', window.scrollY > 0);
      }
      this.syncHeaderMetrics();
    };
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });

    /* Mobile drawer toggles (hamburger + the drawer's own close button). */
    if (this.drawer) {
      this.drawerToggles.forEach((btn) => {
        btn.addEventListener('click', () => this.setDrawer(!this.drawerOpen));
      });
    }

    /* Toggling a dropdown shut is intercepted so the panel can fade out; the
       native collapse would un-render it on the spot. Delegated from the host
       so panels added by a section re-render are covered, and scoped to
       [data-dropdown] so the drawer's [data-submenu] rows keep the native
       behaviour (their motion is the drawer's, not this). */
    this.onDropdownToggleClick = (event) => {
      const summary = event.target.closest('summary');
      if (!summary) return;
      const details = summary.closest('details');
      if (!details || !details.matches('details[data-dropdown]')) return;
      if (details.hasAttribute('data-closing')) {
        /* Clicked again mid-fade: the panel is still on screen, so treat it as
           "keep it" rather than replaying open on a half-faded box. */
        event.preventDefault();
        this.#cancelClose(details);
      } else if (details.hasAttribute('open')) {
        event.preventDefault();
        this.#closeDropdown(details);
      }
    };
    this.addEventListener('click', this.onDropdownToggleClick);

    /* Close dropdown <details> on click-away / Escape; close drawer on
       resize to desktop. */
    this.onDocClick = (event) => {
      this.querySelectorAll('details[data-dropdown][open]').forEach((details) => {
        if (!details.contains(event.target)) this.#closeDropdown(details);
      });
      if (this.drawerOpen && !this.contains(event.target)) this.setDrawer(false);
    };
    document.addEventListener('click', this.onDocClick);

    /* Tabbing out of an open dropdown closes it too — click-away only covers
       pointers, so keyboard users were left with a panel covering the page. */
    this.onFocusOut = (event) => {
      /* relatedTarget null means focus went NOWHERE — the browser's focus fixup
         after a press on a non-focusable element (the panel's own padding:
         <details> is not focusable, only its <summary>), or the window losing
         focus. Without this guard, clicking the panel's own whitespace closed
         it. The Tab-out this handler exists for always carries a real
         relatedTarget; Escape and click-away are covered by onKeyup and
         onDocClick. */
      if (!event.relatedTarget) return;
      this.querySelectorAll('details[data-dropdown][open]').forEach((details) => {
        if (!details.contains(event.relatedTarget)) this.#closeDropdown(details);
      });
    };
    this.addEventListener('focusout', this.onFocusOut);

    this.onKeyup = (event) => {
      if (event.key !== 'Escape') return;
      this.querySelectorAll('details[data-dropdown][open]').forEach((d) => {
        const hadFocus = d.contains(document.activeElement);
        this.#closeDropdown(d);
        /* Focus goes back now, not when the fade ends: Escape has to feel
           instant to a keyboard user, and the summary is still in the DOM. */
        if (hadFocus) d.querySelector('summary')?.focus();
      });
      if (this.drawerOpen) {
        this.setDrawer(false);
        if (this.drawerToggle) this.drawerToggle.focus();
      }
    };
    window.addEventListener('keyup', this.onKeyup);

    this.onResize = () => {
      /* A viewport change can reflow the announcement bar above the header
         without changing the header's own box, so re-publish the metrics. */
      this.syncHeaderMetrics();
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        if (window.innerWidth >= 990 && this.drawerOpen) this.setDrawer(false);
      }, 250);
    };
    window.addEventListener('resize', this.onResize);

    this.onFocusTrap = (event) => {
      /* Trap inside the drawer itself, not the whole header-nav: the drawer is
         role="dialog" aria-modal="true", so everything outside it is removed
         from the accessibility tree — a Tab ring that still visited the
         hamburger, the logo and the mobile cart link would land focus on nodes
         no screen reader can announce. The drawer carries its own close button
         ([data-drawer-toggle]) so the exit stays reachable. Exception: a
         pushed-in submenu is an opaque full-drawer layer, so the rows behind it
         must not take focus (its back button and Escape are the way out). */
      if (event.key !== 'Tab' || !this.drawerOpen) return;
      if (!this.drawer) return;
      /* :not([data-closing]) — during closeSubmenu's exit hold the panel is
         still [open] but inert, so without this the trap kept preventDefault-ing
         Tab and focusing an unfocusable button: every keypress for those 200ms
         did nothing. Falling through here lets Tab walk the drawer rows that are
         already being revealed underneath. */
      const panel = this.drawer.querySelector(
        'details[data-submenu][open]:not([data-closing]) .header__submenu-panel'
      );
      if (!panel) {
        trapFocus(this.drawer, event);
        return;
      }
      if (!panel.contains(document.activeElement)) {
        /* Focus sits on the covered drawer row that opened the panel — pull it
           inside instead of letting Tab walk along the obscured rows. */
        const entry = panel.querySelector('[data-submenu-back], a[href], button:not([disabled])');
        if (entry) {
          event.preventDefault();
          entry.focus();
        }
        return;
      }
      trapFocus(panel, event);
    };
    document.addEventListener('keydown', this.onFocusTrap);

    /* Back row inside a pushed-in mobile submenu closes it. Closing un-renders
       the focused button, so hand focus back to the summary that opened the
       submenu — otherwise it falls to <body> and Tab escapes the modal drawer
       (same restore as the Escape handler above). */
    this.addEventListener('click', (event) => {
      const back = event.target.closest('[data-submenu-back]');
      if (!back) return;
      const details = back.closest('details');
      if (!details) return;
      const hadFocus = details.contains(document.activeElement);
      /* Focus goes back NOW, not when the push-out ends: the button is about to
         be inerted and the summary is already in the DOM, so waiting would drop
         focus to <body> for the length of the animation. */
      if (hadFocus) details.querySelector('summary')?.focus();
      this.closeSubmenu(details);
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.metricsFrame) window.cancelAnimationFrame(this.metricsFrame);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('keyup', this.onKeyup);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onFocusTrap);
    /* Pending timers would touch a detached drawer after a section re-render. */
    window.clearTimeout(this.resizeTimer);
    window.clearTimeout(this.submenuResetTimer);
    /* Cancel every in-flight close AND undo the half-applied state it left.
       This element can be re-inserted (the theme editor moves sections), and a
       surviving [data-closing] would come back as an open-but-transparent
       panel covering the page, still inert and with no timer left to clear it. */
    this.#closeTimers.forEach((timer, details) => {
      window.clearTimeout(timer);
      const panel = details.querySelector('.header__panel');
      if (panel) panel.inert = false;
      details.removeAttribute('data-closing');
      details.removeAttribute('open');
    });
    this.#closeTimers.clear();
    /* Re-inserting the element runs connectedCallback again. Without this the
       toggle interceptor would be bound twice and the two copies would pair up
       — the first closes, the second immediately cancels — leaving the
       dropdown permanently unclosable. */
    this.removeEventListener('click', this.onDropdownToggleClick);
    unlockScroll(this);
  }

  /* Play a dropdown's exit, then collapse it. <details> stops rendering its
     content the moment `open` is gone, so the fade cannot be pure CSS: hold
     `open` for one more transition cycle with [data-closing] on, and let the
     CSS in header.liquid key the fade off that attribute.

     The duration is read back from the computed style rather than hard-coded —
     same contract as setDrawer's submenu reset — so reduced motion (which
     zeroes --header-panel-duration) collapses the panel synchronously with no
     perceptible lag, and a future change to the CSS value needs no JS edit. */
  /* Mirror of #closeDropdown for the drawer's pushed-in submenu: <details>
     un-renders its panel the moment `open` goes away, so the exit animation
     needs the attribute held for one cycle. `instant` is for the drawer closing
     underneath it, where there is nothing left to watch the panel slide. */
  closeSubmenu(details, instant) {
    if (!details.hasAttribute('open')) return;
    const panel = details.querySelector('.header__submenu-panel');
    const timers = this.submenuTimers || (this.submenuTimers = new Map());
    window.clearTimeout(timers.get(details));
    timers.delete(details);
    details.removeAttribute('data-closing');
    if (!panel || instant) {
      if (panel) panel.inert = false;
      details.removeAttribute('open');
      return;
    }
    /* Flag FIRST, then measure — the exit keyframe only exists under
       [data-closing]. Read animationDuration, never the raw --submenu-duration
       custom property: that is authored in MILLISECONDS ("200ms") while the
       computed animation property is always normalised to seconds, and parsing
       the raw value as seconds held the panel open for 200s. */
    details.setAttribute('data-closing', '');
    const seconds = parseFloat(getComputedStyle(panel).animationDuration);
    const ms = Number.isFinite(seconds) ? seconds * 1000 : 0;
    if (ms <= 0) {
      details.removeAttribute('data-closing');
      panel.inert = false;
      details.removeAttribute('open');
      return;
    }
    /* Still painted while it slides out, so still clickable and still in the
       Tab ring without this — same reasoning as the dropdown panel. */
    panel.inert = true;
    timers.set(
      details,
      window.setTimeout(() => {
        timers.delete(details);
        panel.inert = false;
        details.removeAttribute('data-closing');
        details.removeAttribute('open');
      }, ms)
    );
  }

  #closeDropdown(details) {
    if (!details.hasAttribute('open') || details.hasAttribute('data-closing')) return;
    const panel = details.querySelector('.header__panel');
    /* Flag FIRST, then measure. The reference leaves in half the time it enters
       (Alpine's x-transition default: 150ms in, 75ms out), and the theme now
       mirrors that by overriding --header-panel-duration inside the
       [data-closing] rule — so reading the duration before setting the flag
       would hold `open` for the ENTER time and leave a finished panel sitting
       inert for another 75ms. */
    details.setAttribute('data-closing', '');
    const seconds = panel ? parseFloat(getComputedStyle(panel).transitionDuration) : 0;
    const ms = Number.isFinite(seconds) ? seconds * 1000 : 0;
    if (ms <= 0) {
      details.removeAttribute('data-closing');
      details.removeAttribute('open');
      return;
    }
    /* A panel that is fading out is still painted, so for the whole 150ms it
       would still take clicks and still sit in the Tab ring. `inert` closes
       both holes at once — pointer-events would leave the keyboard one open.
       It goes on the PANEL, never on the <details>: inerting the details would
       disable the <summary> too, and cancelling the close is a click on it. */
    if (panel) panel.inert = true;
    this.#closeTimers.set(
      details,
      window.setTimeout(() => {
        this.#closeTimers.delete(details);
        if (panel) panel.inert = false;
        details.removeAttribute('data-closing');
        details.removeAttribute('open');
      }, ms)
    );
  }

  /* Abandon a close already in flight and leave the panel open. */
  #cancelClose(details) {
    window.clearTimeout(this.#closeTimers.get(details));
    this.#closeTimers.delete(details);
    const panel = details.querySelector('.header__panel');
    if (panel) panel.inert = false;
    details.removeAttribute('data-closing');
  }

  get drawerOpen() {
    return this.drawerToggle ? this.drawerToggle.getAttribute('aria-expanded') === 'true' : false;
  }

  setDrawer(open) {
    if (!this.drawer || !this.drawerToggle) return;
    /* Resync before the drawer paints: --header-bottom drives its offset and
       height, and the announcement bar above the header can change height
       without resizing the header's own box (so neither the ResizeObserver nor
       a scroll fires). Cheap — the sync is rAF-batched. */
    if (this.syncHeaderMetrics) this.syncHeaderMetrics();
    /* Closing while focus sits on a control inside the drawer (its own close
       button, a submenu row) would drop focus to <body> once the drawer goes
       visibility: hidden — hand it back to the hamburger instead. */
    const focusWasInside = this.drawer.contains(document.activeElement);
    /* Only the hamburger describes a collapsible region; the in-drawer close
       button is a plain close control and carries no aria-expanded. */
    this.drawerToggles.forEach((btn) => {
      if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', String(open));
    });
    this.drawer.toggleAttribute('data-open', open);
    if (open) lockScroll(this);
    else unlockScroll(this);
    if (open) {
      const first = this.drawer.querySelector('a[href], button:not([disabled]), summary');
      if (first) first.focus();
    }
    if (!open) {
      if (focusWasInside && this.drawerToggle) this.drawerToggle.focus();
      /* Reset any pushed-in submenus after the close transition — the drawer's
         own --drawer-duration (0s under reduced motion), read from the computed
         style rather than hard-coded. Reset is INSTANT: the drawer has already
         slid away, so there is nothing left to watch the panel push out, and
         closeSubmenu also clears any exit timer and inert flag a half-finished
         close left behind. */
      const seconds = parseFloat(getComputedStyle(this.drawer).transitionDuration);
      const ms = (Number.isFinite(seconds) ? seconds : 0.25) * 1000;
      window.clearTimeout(this.submenuResetTimer);
      this.submenuResetTimer = window.setTimeout(() => {
        this.drawer.querySelectorAll('details[data-submenu][open]').forEach((d) => this.closeSubmenu(d, true));
      }, ms + 50);
    }
  }
}
if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);

/* --- <age-popup> — session-scoped welcome modal ---------------------------- */
class AgePopup extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.storageKey || 'fs-age-popup-seen';
    this.dialog = this.querySelector('[data-popup]');
    if (!this.dialog) return;

    /* Wire every close path BEFORE the "already seen" bail-out: open() is also
       called from the theme editor, and those handlers have to exist by then. */
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-close]') || event.target.matches('[data-popup-overlay]')) {
        this.close();
      }
    });
    this.onKeyup = (event) => {
      if (event.key === 'Escape') this.close();
    };
    this.onFocusTrap = (event) => {
      if (event.key === 'Tab' && this.hasAttribute('data-open')) trapFocus(this, event);
    };

    let seen = null;
    try {
      seen = window.sessionStorage.getItem(this.key);
    } catch (error) {
      seen = '1'; /* storage unavailable (private mode) -> never nag */
    }
    /* In the theme editor the popup is previewed by selecting its section (see
       the shopify:section:select handler below). Auto-opening on every preview
       reload would steal the scroll lock while the merchant edits other
       sections — and the session flag is deliberately never written here, so it
       would happen on every single reload. */
    if (window.Shopify && window.Shopify.designMode) return;
    if (seen) return; /* guards only the auto-open timer */

    const delay = Number(this.dataset.delay || 2) * 1000;
    this.timer = window.setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    window.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('keydown', this.onFocusTrap);
    /* A section re-render discards an open popup — don't leave the page locked. */
    this.removeAttribute('data-open');
    unlockScroll(this);
  }

  open() {
    /* Don't burn the session flag while a merchant is editing the section. */
    if (!(window.Shopify && window.Shopify.designMode)) {
      try {
        window.sessionStorage.setItem(this.key, '1');
      } catch (error) {
        /* ignore */
      }
    }
    /* Only one modal may trap Tab at a time. Two live traps fight over every
       keypress and pin focus on the topmost dialog's first control, so the
       popup (z-index 90, above both) collapses the others as it opens —
       the same precedent CartDrawer.open() sets for the nav drawer. */
    document.querySelectorAll('header-nav, mega-header').forEach((nav) => {
      if (typeof nav.setDrawer === 'function' && nav.drawerOpen) nav.setDrawer(false);
    });
    document.querySelectorAll('cart-drawer[data-open]').forEach((drawer) => {
      if (typeof drawer.close === 'function') drawer.close();
    });
    /* A showModal() dialog owns the top layer and makes the rest of the
       document inert, so the popup would open behind it and its close button
       could not take focus. */
    document.querySelectorAll('dialog[open]').forEach((modal) => {
      /* :modal, not [open] — a non-modal dialog (an app's review or wishlist
         panel) is not in the top layer and loses to the popup on z-index, so
         closing it would be collateral damage. */
      if (typeof modal.close === 'function' && modal.matches(':modal')) modal.close();
    });
    this.opener = document.activeElement;
    this.setAttribute('data-open', '');
    lockScroll(this);
    window.addEventListener('keyup', this.onKeyup);
    document.addEventListener('keydown', this.onFocusTrap);
    const focusable = this.querySelector('[data-popup-close]');
    if (focusable) focusable.focus();
  }

  close() {
    window.clearTimeout(this.timer); /* a manual close beats a pending auto-open */
    this.removeAttribute('data-open');
    unlockScroll(this);
    window.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('keydown', this.onFocusTrap);
    if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) {
      this.opener.focus();
    }
  }
}
if (!customElements.get('age-popup')) customElements.define('age-popup', AgePopup);

/* --- <scroll-highlight> — lift whichever child is nearest the viewport middle
   The reference's safety page runs this verbatim on its .scroll-block elements:
   on every scroll frame it measures each block's vertical midpoint, dims them
   all, and lifts the one closest to innerHeight / 2. Reproduced here as state
   (a [data-active] attribute) rather than the reference's inline styles, so the
   colours stay scheme-driven and reduced motion can drop the transition.

   The reference queues a fresh rAF per scroll event; this coalesces to one
   frame, which is the same picture with less work. */
class ScrollHighlight extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-scroll-highlight-item]'));
    /* Below two there is no "nearest one" worth dimming the others for. */
    if (this.items.length < 2) return;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.toggleAttribute('data-live', true);
    this.sync();
  }

  disconnectedCallback() {
    if (!this.onScroll) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  sync() {
    const trigger = window.innerHeight / 2;
    let best = null;
    let bestDistance = Infinity;
    this.items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - trigger);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = item;
      }
    });
    this.items.forEach((item) => item.toggleAttribute('data-active', item === best));
  }
}
if (!customElements.get('scroll-highlight')) customElements.define('scroll-highlight', ScrollHighlight);

/* --- <accordion-group> — <details> list with an animated disclosure --------
   Two jobs: optional exclusive-open (data-exclusive), and animating the panel.

   <details> stops rendering its body the instant `open` goes away, so the
   collapse has to hold the attribute for one transition cycle — the same
   hold-then-release HeaderNav uses for its dropdown. Opening needs the mirror
   of that: the body must exist at its collapsed size for one frame before the
   transition has a start value to animate from.

   A section opts in by marking the body [data-accordion-body] and giving it a
   transition; the duration is read back from the computed style, so reduced
   motion (which zeroes it) collapses instantly with no special case here. A
   list without that marker keeps the plain native behaviour. */
class AccordionGroup extends HTMLElement {
  connectedCallback() {
    /* Server-rendered `open` (a block's "Open by default", or the theme editor
       selecting one) has no transition to play — it is already at rest. */
    this.querySelectorAll('details[open]').forEach((details) => {
      details.setAttribute('data-expanded', '');
    });

    this.addEventListener('click', (event) => {
      const summary = event.target.closest && event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;
      const details = summary.parentElement;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      event.preventDefault();
      /* Clicked again mid-collapse: reverse it rather than ignore the press.
         `open` is still set during the hold, so without this the summary was
         completely dead for the 300ms and the shopper needed two clicks. The
         reference just rewrites maxHeight/opacity, so the same second click
         reverses the panel there instantly. */
      if (details.hasAttribute('data-collapsing')) {
        this.expand(details);
        return;
      }
      /* Opening otherwise rides the native toggle below; only the close needs
         taking over, and this covers the keyboard too (Enter/Space fire
         click). */
      this.collapse(details);
    });

    this.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !this.contains(details)) return;
        if (!details.open) return;
        this.expand(details);
        if (this.dataset.exclusive === 'false') return;
        this.querySelectorAll('details[open]').forEach((other) => {
          if (other !== details) this.collapse(other);
        });
      },
      true
    );
  }

  disconnectedCallback() {
    this.querySelectorAll('details[data-collapsing]').forEach((details) => {
      window.clearTimeout(Number(details.dataset.collapseTimer));
      delete details.dataset.collapseTimer;
      details.removeAttribute('data-collapsing');
    });
  }

  /* Milliseconds the body takes to collapse, straight from the stylesheet, so
     the section owns the timing and reduced motion needs no branch here. */
  bodyDuration(details) {
    const body = details.querySelector('[data-accordion-body]');
    if (!body) return 0;
    const styles = getComputedStyle(body);
    return ((parseFloat(styles.transitionDuration) || 0) + (parseFloat(styles.transitionDelay) || 0)) * 1000;
  }

  expand(details) {
    /* Re-opening mid-collapse: drop the pending release or it would slam the
       panel shut a moment after the shopper re-opened it. */
    this.clearCollapse(details);
    if (details.hasAttribute('data-expanded')) return;
    /* One frame at the collapsed size gives the transition a start value. */
    window.requestAnimationFrame(() => {
      if (details.open) details.setAttribute('data-expanded', '');
    });
  }

  collapse(details) {
    if (!details.open || details.hasAttribute('data-collapsing')) return;
    details.removeAttribute('data-expanded');
    const ms = this.bodyDuration(details);
    if (!ms) {
      details.open = false;
      return;
    }
    details.setAttribute('data-collapsing', '');
    details.dataset.collapseTimer = String(
      window.setTimeout(() => {
        this.clearCollapse(details);
        details.open = false;
      }, ms)
    );
  }

  clearCollapse(details) {
    if (details.dataset.collapseTimer) {
      window.clearTimeout(Number(details.dataset.collapseTimer));
      delete details.dataset.collapseTimer;
    }
    details.removeAttribute('data-collapsing');
  }
}
if (!customElements.get('accordion-group')) customElements.define('accordion-group', AccordionGroup);

/* --- Quantity inputs: auto-submit the closest cart form on change ---------- */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement) || !input.matches('[data-quantity-input]')) return;
  /* An emptied field would submit updates[]='' — which the Cart API reads as 0
     and silently deletes the line. Restore the last known quantity instead;
     removing is what the explicit remove button is for. */
  if (input.value.trim() === '') {
    /* defaultValue is the server-rendered quantity, so the field goes back to
       what the cart actually holds even without a data-previous-value hint. */
    input.value = input.dataset.previousValue || input.defaultValue || '1';
    return;
  }
  const form = input.closest('form');
  if (form && !input.closest('cart-drawer')) {
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit(); /* Safari <=15 fallback */
  }
});

/* --- Marquee pause/play toggle (keyboard + touch accessible) --------------- */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-marquee-toggle]');
  if (!toggle) return;
  const region = toggle.closest('[data-marquee-region]');
  if (!region) return;
  const paused = region.toggleAttribute('data-paused');
  toggle.setAttribute('aria-pressed', String(paused));
});

/* --- Marquee speed: a constant pixels-per-second, not a fixed duration ------
   Every marquee on the source site integrates a hard-coded px/s against the
   frame delta — `let speed = 60` in marquee_v2, data-speed="0.8" per rAF tick
   (= 48 px/s at 60Hz) in marquee_qcpmje — so the strip travels at the same
   physical rate on a phone as on a 1440px desktop, and adding blocks lengthens
   the cycle instead of speeding it up.

   A CSS keyframe animating translateX(-50%) over a fixed duration does the
   opposite: px/s is proportional to the copy's width. Measured, the quotes row
   ran 45.7 px/s on desktop and 24.9 px/s on a phone (the mobile card is much
   narrower), and the product band was 11% fast on desktop while being 20% slow
   below 990px — one duration cannot satisfy both. So the duration is derived
   here instead, from the width the browser actually laid out.

   The track holds two identical copies and the keyframe travels -50%, so one
   cycle covers scrollWidth / 2. The Liquid-rendered --marquee-duration on the
   section stays as the no-JS fallback; this overrides it on the track itself. */
function syncMarqueeSpeed(track) {
  const speed = Number(track.dataset.marqueeSpeed);
  if (!(speed > 0)) return;
  /* Two copies -> one cycle is half the laid-out width. Transforms do not
     affect layout, so the running animation does not perturb this. */
  const distance = track.scrollWidth / 2;
  if (!(distance > 0)) return;
  const seconds = distance / speed;
  /* Re-declaring animation-duration mid-flight remaps the current time and the
     strip visibly jumps, and late-loading images resize the track more than
     once. Only act on a change big enough to be worth that jump. */
  const previous = Number(track.dataset.marqueeDurationApplied);
  if (previous && Math.abs(seconds - previous) / previous < 0.01) return;
  track.dataset.marqueeDurationApplied = String(seconds);
  track.style.setProperty('--marquee-duration', `${seconds.toFixed(3)}s`);
}

function syncAllMarqueeSpeeds(root) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  scope.querySelectorAll('.marquee-track[data-marquee-speed]').forEach(syncMarqueeSpeed);
}

/* The track is width: max-content, so its own box changes exactly when its
   content's width does — late images, a webfont swap, a breakpoint. One
   observer covers all three; without it the first paint's measurement (taken
   before the brand fonts land) would stick for the session. */
if ('ResizeObserver' in window) {
  const marqueeSpeedObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => syncMarqueeSpeed(entry.target));
  });
  const observeMarquees = (root) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('.marquee-track[data-marquee-speed]').forEach((track) => {
      marqueeSpeedObserver.observe(track);
    });
  };
  observeMarquees();
  document.addEventListener('shopify:section:load', (event) => observeMarquees(event.target));
} else {
  syncAllMarqueeSpeeds();
  window.addEventListener('resize', () => syncAllMarqueeSpeeds());
  document.addEventListener('shopify:section:load', (event) => syncAllMarqueeSpeeds(event.target));
}

/* --- Marquee: drag to reverse -------------------------------------------
   The source's stats band (marquee_v2) integrates `direction * speed * delta`
   and carries an explicit "Drag to change direction" block: mousedown records
   clientX, mouseup sets `direction = deltaX < 0 ? -1 : 1`. The CSS keyframe
   here already runs both ways via [data-direction='reverse'], so only the
   gesture is missing.

   Two deliberate departures from the source: a movement threshold (its test is
   `deltaX < 0`, so a plain CLICK — deltaX exactly 0 — takes the false branch
   and flips the band, which reads as a bug rather than a feature), and mouse
   only, because on a touch device the same gesture is how the page is scrolled.
   Opt-in per section via data-marquee-drag. */
const MARQUEE_DRAG_THRESHOLD = 8;
document.addEventListener('pointerdown', (event) => {
  const region = event.target.closest && event.target.closest('[data-marquee-drag]');
  if (!region || event.pointerType !== 'mouse' || event.button !== 0) return;
  region.dataset.marqueeDragX = String(event.clientX);
});
document.addEventListener('pointerup', (event) => {
  const region = event.target.closest && event.target.closest('[data-marquee-drag]');
  if (!region || region.dataset.marqueeDragX === undefined) return;
  const deltaX = event.clientX - Number(region.dataset.marqueeDragX);
  delete region.dataset.marqueeDragX;
  if (Math.abs(deltaX) < MARQUEE_DRAG_THRESHOLD) return;
  /* Under reduced motion the track is not animating at all — it is a manually
     scrolled strip — so there is no direction to flip. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  region.querySelectorAll('.marquee-track').forEach((track) => {
    /* Compose with the row's AUTHORED direction rather than replacing it (the
       quotes band alternates rows, so a flip means "the other way from whatever
       this row was"). The authored value has to be stashed on first use —
       reading data-direction after the first flip would read our own output and
       every further flip would compound. */
    if (track.dataset.directionAuthored === undefined) {
      track.dataset.directionAuthored = track.dataset.direction === 'reverse' ? 'reverse' : 'normal';
    }
    const authoredReverse = track.dataset.directionAuthored === 'reverse';
    const flipped = deltaX > 0;
    track.dataset.direction = authoredReverse !== flipped ? 'reverse' : 'normal';
  });
});

/* --- Marquee: scroll-container semantics only while the animation is off ---- */
/* .marquee-viewport is overflow: hidden while the strip animates — a tabindex
   there would be an empty tab stop announced as a region the arrow keys cannot
   move. Only under prefers-reduced-motion does base.css turn it into a real
   horizontal scroll container, and only then does it owe a keyboard path and a
   name (WCAG 2.1.1 / 4.1.2). The sections render the localized name into
   data-marquee-label and this applies the attributes to match the media query
   (including when the merchant flips the OS setting mid-visit). */
const marqueeReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
function syncMarqueeScrollables() {
  const scrollable = marqueeReduceMotion.matches;
  document.querySelectorAll('[data-marquee-label]').forEach((viewport) => {
    const label = viewport.dataset.marqueeLabel;
    if (scrollable && label) {
      viewport.setAttribute('tabindex', '0');
      viewport.setAttribute('role', 'group');
      viewport.setAttribute('aria-label', label);
    } else {
      viewport.removeAttribute('tabindex');
      viewport.removeAttribute('role');
      viewport.removeAttribute('aria-label');
    }
  });
}
syncMarqueeScrollables();
if (typeof marqueeReduceMotion.addEventListener === 'function') {
  marqueeReduceMotion.addEventListener('change', syncMarqueeScrollables);
} else if (typeof marqueeReduceMotion.addListener === 'function') {
  marqueeReduceMotion.addListener(syncMarqueeScrollables); /* Safari <= 13 */
}
/* Section re-renders (theme editor, section rendering API) ship fresh viewports
   without the attributes. */
document.addEventListener('shopify:section:load', syncMarqueeScrollables);

/* --- Overflow scrollers: keyboard semantics only while they overflow -------- */
/* The account / order tables sit in an overflow-x container with a min-width,
   so they scroll at narrow widths and not at wide ones. A static tabindex would
   leave a dead tab stop on desktop, so the attributes follow the measured
   overflow (WCAG 2.1.1 / 4.1.2); the sections render the localized name into
   data-scroll-label. */
function syncOverflowScrollables() {
  document.querySelectorAll('[data-scroll-label]').forEach((el) => {
    const label = el.dataset.scrollLabel;
    if (label && el.scrollWidth > el.clientWidth) {
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'group');
      el.setAttribute('aria-label', label);
    } else {
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
    }
  });
}
syncOverflowScrollables();
/* Web fonts land after parse and can push the table past its container. */
window.addEventListener('load', syncOverflowScrollables);
let overflowScrollTimer;
window.addEventListener('resize', () => {
  window.clearTimeout(overflowScrollTimer);
  overflowScrollTimer = window.setTimeout(syncOverflowScrollables, 150);
});
document.addEventListener('shopify:section:load', syncOverflowScrollables);

/* --- Theme editor: preview hidden overlay sections on select --------------- */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    const popup = event.target.querySelector('age-popup');
    if (popup && typeof popup.open === 'function') popup.open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.querySelector('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    const popup = event.target.querySelector('age-popup');
    if (popup && typeof popup.close === 'function') popup.close();
  });

  /* Marquee rows are CSS-animated strips inside an overflow: hidden viewport, so
     a selected block is off screen most of the loop (row 2 runs reverse, which
     parks a whole copy to the side at the very frame the editor re-renders into).
     The editor requires a selected block to come into view and STAY in view —
     the same rule that makes a slideshow slide to the selected slide and pause
     while it is selected — so park the track on the selection and hold it there
     until deselect. Shared by every .marquee-track, not just the quotes rows. */
  /* Which element each parked track is parked FOR, plus the animation phase it
     was holding. Four elements per row now carry the same block id, so the editor
     can fire select on more than one of them, and a deselect for the previous
     block can land after the select for the next one. */
  const parkedMarquees = new Map();

  /* A re-rendered section leaves its old track detached and nothing else would
     ever drop the entry — the Map would pin the whole detached subtree. */
  const forgetDetachedMarquees = () => {
    parkedMarquees.forEach((record, track) => {
      if (!track.isConnected || !record.target.isConnected) parkedMarquees.delete(track);
    });
  };

  /* Where the track has to sit for `target` to be inside `viewport`, or null when
     there is nothing to aim at.

     Read the geometry BEFORE the caller kills the animation: an animation's
     computed value beats an inline transform in the cascade, so the offset has to
     be written together with `animation: none`, and clearing the animation first
     would snap the track back to transform: none and hand back coordinates it no
     longer holds. The offset is incremental (current translateX plus the gap
     between where the block is and where it should sit), so it never assumes a
     sign or a writing direction — RTL runs the mirrored fs-marquee-rtl keyframe
     and lands in the same place. */
  const marqueeOffsetFor = (track, viewport, target) => {
    const rect = target.getBoundingClientRect();
    /* A display: none block has no geometry to aim at — under reduced motion
       base.css drops the whole second copy and the section drops the repeated
       half. Centring a 0×0 rect would shove the track by half a viewport and take
       the row off screen, so leave the track alone instead. */
    if (!rect.width && !rect.height) return null;
    const vp = viewport.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();
    const style = getComputedStyle(track);
    const current =
      style.transform && style.transform !== 'none' ? new DOMMatrixReadOnly(style.transform).m41 : 0;
    /* Centred while it fits. When it does not, it goes flush with the viewport's
       START edge — which is the RIGHT edge in RTL, so the direction is asked for
       rather than assumed. */
    const room = vp.width - rect.width;
    const inset = room > 0 ? room / 2 : style.direction === 'rtl' ? room : 0;
    const offset = current + (vp.left + inset - rect.left);
    /* Neither end of the track may pull away from the viewport: centring one of
       the last cards would otherwise leave blank space inside the clip, and under
       reduced motion — where .marquee-viewport is a real overflow-x: auto
       scroller — an unclamped transform would desync it from scrollLeft and put
       part of the row out of reach. `base` is where the track sits with no
       transform at all. A track narrower than its viewport has no valid range
       (the two bounds cross), so it keeps the unclamped offset. */
    if (trackRect.width < vp.width) return offset;
    const base = trackRect.left - current;
    return Math.min(Math.max(offset, vp.right - base - trackRect.width), vp.left - base);
  };

  const parkMarqueeTrack = (target) => {
    const track = target.closest && target.closest('.marquee-track');
    if (!track) return;
    const viewport = track.closest('.marquee-viewport');
    if (!viewport) return;
    forgetDetachedMarquees();
    const parked = parkedMarquees.get(track);
    /* Already parked for this very element: re-running would re-read a phase the
       track no longer has (the animation is gone) and re-measure from a position
       this handler chose rather than the one the animation left. */
    if (parked && parked.target === target) return;
    /* The phase has to come off the live animation before `animation: none`
       destroys it — and survive a re-park, when there is none left to read. */
    const animation = track.getAnimations()[0];
    const time = parked ? parked.time : animation ? animation.currentTime : null;
    const offset = marqueeOffsetFor(track, viewport, target);
    if (offset === null) return;
    track.style.animation = 'none';
    track.style.transform = `translateX(${offset}px)`;
    parkedMarquees.set(track, { target, time });
  };

  const releaseMarqueeTrack = (target) => {
    const track = target.closest && target.closest('.marquee-track');
    if (!track) return;
    const parked = parkedMarquees.get(track);
    /* A late deselect for the previous block must not undo the parking the next
       block's select has already done. */
    if (!parked || parked.target !== target) return;
    parkedMarquees.delete(track);
    /* Both halves of the override go, or the stylesheet only gets half its rule
       back: the track would restart its animation from a transform it never
       chose. Empty strings, so the cascade — not this handler — decides. */
    track.style.animation = '';
    track.style.transform = '';
    /* Clearing the inline animation builds a NEW animation, and a new one starts
       at t=0 — row 2 would snap back to translateX(-3198px) the moment the
       merchant clicks away. Put the phase back where the parking found it. */
    if (parked.time === null) return;
    const animation = track.getAnimations()[0];
    if (animation) animation.currentTime = parked.time;
  };

  /* The editor's device-preview switch resizes the iframe, which moves the
     viewport out from under a parked offset: the selected block drifts out of
     view and the "stays in view" rule breaks while it is still selected. */
  const reparkMarquees = () => {
    forgetDetachedMarquees();
    parkedMarquees.forEach((record, track) => {
      const viewport = track.closest('.marquee-viewport');
      if (!viewport) return;
      const offset = marqueeOffsetFor(track, viewport, record.target);
      if (offset === null) return;
      track.style.transform = `translateX(${offset}px)`;
    });
  };
  let marqueeResizeTimer;
  window.addEventListener('resize', () => {
    if (!parkedMarquees.size) return;
    window.clearTimeout(marqueeResizeTimer);
    marqueeResizeTimer = window.setTimeout(reparkMarquees, 150);
  });

  /* The header's mobile promo block only exists inside the drawer, so selecting
     it in the editor has to open the drawer or there is nothing to preview
     (header.liquid unhides it at desktop widths while design mode is on). */
  document.addEventListener('shopify:block:select', (event) => {
    const nav = event.target.closest && event.target.closest('header-nav');
    if (nav && typeof nav.setDrawer === 'function' && !nav.drawerOpen) nav.setDrawer(true);
    /* Same story for the cart drawer's upsell / promo blocks: they only exist
       inside the closed panel. No block:deselect counterpart — the section-level
       deselect already closes it, and closing here would slam the drawer shut as
       the merchant steps between upsell blocks. */
    const drawer = event.target.closest && event.target.closest('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    /* Last: the branches above are the older, load-bearing ones, and a throw in
       the marquee geometry must not cost them their run. */
    parkMarqueeTrack(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const nav = event.target.closest && event.target.closest('header-nav');
    if (nav && typeof nav.setDrawer === 'function' && nav.drawerOpen) nav.setDrawer(false);
    releaseMarqueeTrack(event.target);
  });
}
